"use client"

import Accordion from "./accordion"
import { HttpTypes } from "@medusajs/types"

const SUPPLIER_NAMES: Record<string, string> = {
  carrara: "Carrara Stone Co. — Newark, NJ",
  brooklyn: "Brooklyn Clay Works — Brooklyn, NY",
  andes: "Andes Porcelain — Miami, FL",
}

type ProductTabsProps = {
  product: HttpTypes.StoreProduct
}

const ProductTabs = ({ product }: ProductTabsProps) => {
  const tabs = [
    {
      label: "Product Information",
      component: <ProductInfoTab product={product} />,
    },
    {
      label: "Shipping & Returns",
      component: <ShippingInfoTab product={product} />,
    },
  ]

  return (
    <div className="w-full">
      <Accordion type="multiple">
        {tabs.map((tab, i) => (
          <Accordion.Item
            key={i}
            title={tab.label}
            headingSize="medium"
            value={tab.label}
          >
            {tab.component}
          </Accordion.Item>
        ))}
      </Accordion>
    </div>
  )
}

const ProductInfoTab = ({ product }: ProductTabsProps) => {
  const md = (product.metadata ?? {}) as Record<string, any>

  const pricePerSqft =
    md.pricePerSqft != null ? `$${Number(md.pricePerSqft).toFixed(2)}` : null
  const sqftPerBox = md.sqftPerBox != null ? `${Number(md.sqftPerBox)}` : null
  const shipsFrom = md.supplier
    ? SUPPLIER_NAMES[md.supplier] ?? String(md.supplier)
    : null

  const rows: { label: string; value: string | null }[] = [
    { label: "Size", value: md.size ?? null },
    { label: "Material", value: md.material ?? product.material ?? null },
    { label: "Finish", value: md.finish ?? null },
    { label: "Sq Ft / Box", value: sqftPerBox },
    { label: "Price / Sq Ft", value: pricePerSqft },
    { label: "Application", value: md.application ?? md.category ?? null },
    {
      label: "Country of Origin",
      value: md.country ?? product.origin_country ?? null,
    },
    { label: "Ships From", value: shipsFrom },
  ]

  return (
    <div className="text-sm py-8">
      <dl className="flex flex-col divide-y divide-line border-y border-line">
        {rows.map((row) => (
          <div
            key={row.label}
            className="flex items-center justify-between gap-x-4 py-2.5"
          >
            <dt className="font-semibold text-ink">{row.label}</dt>
            <dd className="text-muted text-right">{row.value || "—"}</dd>
          </div>
        ))}
      </dl>
    </div>
  )
}

const ShippingInfoTab = ({ product }: ProductTabsProps) => {
  const md = (product.metadata ?? {}) as Record<string, any>
  const leadTime = md.leadTime || "about 7–12 business days"

  return (
    <div className="text-sm py-8">
      <div className="grid grid-cols-1 gap-y-6">
        <div>
          <span className="font-semibold text-ink">Shipping</span>
          <p className="max-w-prose text-muted mt-1">
            Ships direct from the maker. Boxes are assembled by the maker and
            ship via freight (LTL), curbside delivery — the carrier calls to
            schedule. Estimated to ship in {leadTime}. Samples ship separately
            from our Austin, TX studio in 1–2 business days. If your cart has
            items from different makers, your order may arrive in a few
            separate, tracked shipments.
          </p>
        </div>
        <div>
          <span className="font-semibold text-ink">Returns &amp; damage</span>
          <p className="max-w-prose text-muted mt-1">
            30-day returns (15% restocking, you pay return shipping, original
            packaging). Samples are final sale. Inspect on delivery and report
            shipping damage within 5 days with photos — we replace breakage.
          </p>
        </div>
      </div>
    </div>
  )
}

export default ProductTabs
